import { Crown, Home, Route, Sparkles, Zap, Loader2, Search, Share2, Palette, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SUBSCRIPTION_PRICING } from "@shared/schema";
import { useState } from "react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Badge } from "@/components/ui/badge";
import { useRestyleVisibility, useStripePrices } from "@/lib/featureFlags";
import { useI18n } from "@/lib/i18n";
import { detectUserCountry } from "@/lib/countryDetection";

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  variant?: "default" | "home" | "routes" | "posts" | "route-builder";
}

export function UpgradeModal({ open, onOpenChange, variant = "default" }: UpgradeModalProps) {
  const { t } = useI18n();
  const { canSeeRestyle } = useRestyleVisibility();
  const { getPrice } = useStripePrices();
  const countryCode = detectUserCountry();

  const variants = {
    default: {
      title: t("upgrade.default.title"),
      description: t("upgrade.default.desc"),
      features: [
        { icon: Route, text: t("upgrade.default.feat1") },
        { icon: Users, text: t("upgrade.perk.socialWishlist") },
        { icon: Palette, text: t("upgrade.default.feat3") },
        { icon: Zap, text: t("upgrade.default.feat4") },
        { icon: Home, text: t("upgrade.default.feat5") }
      ]
    },
    home: {
      title: t("home.insider.doorToDoor.title"),
      description: t("home.insider.doorToDoor.desc"),
      features: [
        { icon: Route, text: t("upgrade.home.feat1") },
        { icon: Zap, text: t("upgrade.home.feat2") },
        { icon: Home, text: t("upgrade.home.feat3") }
      ]
    },
    routes: {
      title: t("upgrade.routes.title"),
      description: t("upgrade.routes.desc"),
      features: [
        { icon: Route, text: t("upgrade.routes.feat1") },
        { icon: Sparkles, text: t("upgrade.routes.feat2") },
        { icon: Zap, text: t("upgrade.routes.feat3") }
      ]
    },
    posts: {
      title: t("upgrade.posts.title"),
      description: t("upgrade.posts.desc"),
      features: [
        { icon: Sparkles, text: t("upgrade.posts.feat1") },
        { icon: Crown, text: t("upgrade.posts.feat2") }
      ]
    },
    "route-builder": {
      title: t("upgrade.builder.title"),
      description: t("upgrade.builder.desc"),
      features: [
        { icon: Route, text: t("upgrade.builder.feat1") },
        { icon: Sparkles, text: t("upgrade.builder.feat2") },
        { icon: Zap, text: t("upgrade.builder.feat3") },
        { icon: Home, text: t("upgrade.builder.feat4") }
      ]
    }
  };

  const baseContent = variants[variant];
  const content = {
    ...baseContent,
    features: canSeeRestyle
      ? baseContent.features
      : baseContent.features.filter((f) => !/restyle/i.test(f.text)),
  };
  const [interval, setInterval] = useState<"monthly" | "yearly">("yearly");
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleUpgrade = async () => {
    setIsLoading(true);
    try {
      const countryCode = detectUserCountry();
      const res = await apiRequest("POST", "/api/stripe/checkout/routes-pro", { interval, countryCode });
      const data = await res.json();
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (error: any) {
      console.error(error);
      toast({
        title: "Fout bij laden checkout",
        description: error.message || "Probeer het later opnieuw.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const monthlyPrice = getPrice("insider_monthly", countryCode);
  const yearlyPrice = getPrice("insider_yearly", countryCode);

  const currentPrice = interval === "yearly"
    ? (yearlyPrice?.amount ?? SUBSCRIPTION_PRICING.yearly.price)
    : (monthlyPrice?.amount ?? SUBSCRIPTION_PRICING.monthly.price);

  const isYearly = interval === "yearly";

  // Calculate monthly equivalent for yearly plan
  const monthlyEquivalent = isYearly ? (currentPrice / 12) : currentPrice;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-4 w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
            <Crown className="h-6 w-6 text-primary" />
          </div>
          <DialogTitle className="text-center text-xl">{content.title}</DialogTitle>
          <DialogDescription className="text-center">
            {content.description}
          </DialogDescription>
        </DialogHeader>

        <div className="flex justify-center my-4">
          <ToggleGroup type="single" value={interval} onValueChange={(val) => val && setInterval(val as "monthly" | "yearly")}>
            <ToggleGroupItem value="monthly" className="px-4">{t("upgrade.monthly")}</ToggleGroupItem>
            <ToggleGroupItem value="yearly" className="px-4">
              {t("upgrade.yearly")}
              <Badge variant="secondary" className="ml-2 bg-green-100 text-green-800 hover:bg-green-200">-16%</Badge>
            </ToggleGroupItem>
          </ToggleGroup>
        </div>

        <div className="space-y-3 my-4">
          {content.features.map(({ icon: Icon, text }, i) => (
            <div key={i} className="flex items-center gap-3 text-sm">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <Icon className="h-4 w-4 text-primary" />
              </div>
              <span>{text}</span>
            </div>
          ))}
        </div>

        <div className="text-center my-4 bg-muted/30 p-4 rounded-lg">
          <div className="text-3xl font-bold text-primary flex items-center justify-center gap-1">
            €{currentPrice.toFixed(2).replace(".", ",")}
            <span className="text-base font-normal text-muted-foreground">/{isYearly ? "jaar" : "maand"}</span>
          </div>
          {isYearly && (
            <p className="text-xs text-muted-foreground mt-1">
              Dat is slechts €{(currentPrice / 12).toFixed(2).replace(".", ",")} per maand
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <Button
            size="lg"
            className="w-full bg-magenta hover:bg-magenta/90 text-white font-bold h-12"
            onClick={handleUpgrade}
            disabled={isLoading}
            data-testid="button-upgrade-pro"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t("share.pleaseWait")}
              </>
            ) : (
              t("header.tier.insider")
            )}
          </Button>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            data-testid="button-maybe-later"
            disabled={isLoading}
          >
            {t("upgrade.maybeLater")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
